// Structured builder for the alert rule "condition" DSL evaluated by
// PeakElite's alertService.ts (evaluateTriggerRule / isInRange / checkRating).
// Keep this in sync with that parser - it must produce strings the engine
// can evaluate identically to what an admin sees in the builder.

export type RangeVariable = "WLF" | "HLR" | "Wellness";
export type RangeOperator = "between" | ">" | "<" | "≥" | "≤"; // >, <, ≥, ≤
export type RatingVariable = "sleepRating" | "stressRating";
export type RatingOperator = "=" | ">" | "<" | "≥" | "≤";
export type BooleanVariable = "injured" | "canTrain" | "sickFactor";

export const CYCLE_PHASE_OPTIONS = [
  "menstruation",
  "early_follicular",
  "late_follicular",
  "ovulatory",
  "early_luteal",
  "late_luteal",
] as const;

export type Clause =
  | { type: "range"; variable: RangeVariable; operator: RangeOperator; min: string; max: string; value: string }
  | { type: "cyclePhase"; mode: "equals" | "in"; phases: string[] }
  | { type: "rating"; variable: RatingVariable; operator: RatingOperator; value: string }
  | { type: "boolean"; variable: BooleanVariable; value: boolean };

export const createDefaultClause = (): Clause => ({
  type: "range",
  variable: "WLF",
  operator: "between",
  min: "",
  max: "",
  value: "",
});

export const isClauseComplete = (clause: Clause): boolean => {
  switch (clause.type) {
    case "range":
      return clause.operator === "between"
        ? clause.min.trim() !== "" && clause.max.trim() !== ""
        : clause.value.trim() !== "";
    case "cyclePhase":
      return clause.phases.length > 0;
    case "rating":
      return clause.value.trim() !== "";
    case "boolean":
      return true;
    default:
      return false;
  }
};

const serializeClause = (clause: Clause): string => {
  switch (clause.type) {
    case "range":
      if (clause.operator === "between") {
        return `${clause.variable} ${clause.min}–${clause.max}`;
      }
      return `${clause.variable} ${clause.operator}${clause.value}`;
    case "cyclePhase":
      return clause.mode === "equals"
        ? `cyclePhase=${clause.phases[0]}`
        : `cyclePhase IN (${clause.phases.join(",")})`;
    case "rating": {
      const prefix = clause.operator === "=" ? "" : clause.operator;
      return `${clause.variable} ${prefix}${clause.value}/7`;
    }
    case "boolean":
      return `${clause.variable}=${clause.value}`;
    default:
      return "";
  }
};

export const serializeClauses = (clauses: Clause[]): string =>
  clauses.map(serializeClause).join(" AND ");

const parseRangeClause = (variable: RangeVariable, rest: string): Clause | null => {
  const trimmed = rest.trim();
  if (trimmed.startsWith("≥")) {
    return { type: "range", variable, operator: "≥", min: "", max: "", value: trimmed.substring(1).trim() };
  }
  if (trimmed.startsWith("≤")) {
    return { type: "range", variable, operator: "≤", min: "", max: "", value: trimmed.substring(1).trim() };
  }
  if (trimmed.startsWith(">")) {
    return { type: "range", variable, operator: ">", min: "", max: "", value: trimmed.substring(1).trim() };
  }
  if (trimmed.startsWith("<")) {
    return { type: "range", variable, operator: "<", min: "", max: "", value: trimmed.substring(1).trim() };
  }
  const rangeMatch = trimmed.match(/^([\d.]+)[–-]([\d.]+)$/);
  if (rangeMatch) {
    return { type: "range", variable, operator: "between", min: rangeMatch[1], max: rangeMatch[2], value: "" };
  }
  return null;
};

const parseCyclePhaseClause = (rest: string): Clause | null => {
  const trimmed = rest.trim();
  const inMatch = trimmed.match(/^IN\s*\(([^)]+)\)$/i);
  if (inMatch) {
    const phases = inMatch[1].split(",").map((p) => p.trim()).filter(Boolean);
    if (phases.length === 0) return null;
    return { type: "cyclePhase", mode: "in", phases };
  }
  const eqMatch = trimmed.match(/^=\s*(\w+)$/);
  if (eqMatch) {
    return { type: "cyclePhase", mode: "equals", phases: [eqMatch[1]] };
  }
  return null;
};

const parseRatingClause = (variable: RatingVariable, rest: string): Clause | null => {
  const trimmed = rest.trim();
  const match = trimmed.match(/^([≥≤><]?)(\d+)\/\d+$/);
  if (!match) return null;
  const operator = (match[1] || "=") as RatingOperator;
  return { type: "rating", variable, operator, value: match[2] };
};

const parseBooleanClause = (variable: BooleanVariable, rest: string): Clause | null => {
  const match = rest.trim().match(/^=\s*(true|false)$/i);
  if (!match) return null;
  return { type: "boolean", variable, value: match[1].toLowerCase() === "true" };
};

const parseClause = (part: string): Clause | null => {
  const trimmed = part.trim();
  if (trimmed.startsWith("WLF")) return parseRangeClause("WLF", trimmed.substring(3));
  if (trimmed.startsWith("HLR")) return parseRangeClause("HLR", trimmed.substring(3));
  if (trimmed.startsWith("Wellness")) return parseRangeClause("Wellness", trimmed.substring(8));
  if (trimmed.startsWith("cyclePhase")) return parseCyclePhaseClause(trimmed.substring(10));
  if (trimmed.startsWith("sleepRating")) return parseRatingClause("sleepRating", trimmed.substring(11));
  if (trimmed.startsWith("stressRating")) return parseRatingClause("stressRating", trimmed.substring(12));
  if (trimmed.startsWith("injured")) return parseBooleanClause("injured", trimmed.substring(7));
  if (trimmed.startsWith("canTrain")) return parseBooleanClause("canTrain", trimmed.substring(8));
  if (trimmed.startsWith("sickFactor")) return parseBooleanClause("sickFactor", trimmed.substring(10));
  return null;
};

/**
 * Parses a condition string into structured clauses for the builder UI.
 * Returns null if any part of the condition doesn't match the grammar the
 * alert engine understands (e.g. the freeform descriptions used by the
 * multi-day personalised alerts PA1-PA15, which aren't evaluated from this
 * text at all) - callers should fall back to a raw text editor in that case.
 */
export const parseCondition = (condition: string): Clause[] | null => {
  const parts = condition.split(" AND ").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;

  const clauses: Clause[] = [];
  for (const part of parts) {
    const clause = parseClause(part);
    if (!clause) return null;
    clauses.push(clause);
  }
  return clauses;
};
